import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import MissionForm from "./MissionForm";
import MissionList from "./MissionList";
import MissionFilter from "./MissionFilter";
import MissionEditModal from "./MissionEditModal";
import UpcomingMissions from "./UpcomingMissions";
import { toast } from "react-toastify";
import { FaTruckMoving, FaDownload } from "react-icons/fa";
import { useAuth } from "../Auth/AuthContext";
import { isOrgAdmin } from "../../utils/roles";

const MissionsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isOrgAdmin(user);
  const [missions, setMissions]               = useState([]);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [editModalOpen, setEditModalOpen]     = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [viewMode, setViewMode]               = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [syncing, setSyncing]                 = useState(false);

  const fetchMissions = useCallback(async () => {
    try {
      const res = await api.get("/missions?page=1&limit=100");
      const missionsData = res.data?.data || res.data || [];
      setMissions(missionsData);
      setFilteredMissions(missionsData);
    } catch (err) {
      console.error("❌ Error fetching missions:", err);
      toast.error(t("mission.fetchError") || "Error fetching missions");
    }
  }, [t]);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  const handleCreate = () => {
    setShowCreateModal(false);
    fetchMissions();
  };

  const handleDelete = async (id) => {
    if (!id) { toast.error(t("mission.idUndefined")); return; }
    if (!window.confirm(t("mission.list.confirmDelete"))) return;
    try {
      await api.delete(`/missions/${id}`);
      toast.success(t("mission.deleted") || "Mission deleted");
      fetchMissions();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(t("mission.list.deleteAlreadyRemoved") || "This mission was already deleted.");
        fetchMissions();
      } else {
        console.error("❌ Error deleting mission:", err);
        toast.error(t("mission.deleteError") || "Error deleting mission");
      }
    }
  };

  const handleEdit = (mission) => {
    setSelectedMission(mission);
    setEditModalOpen(true);
  };

  const handleEditSave = async (updatedMission) => {
    try {
      const payload = {
        ...updatedMission,
        priceTTC: (parseFloat(updatedMission.priceHT) * 1.2).toFixed(2),
      };
      await api.put(`/missions/${updatedMission.id}`, payload);
      toast.success(t("mission.updated") || "Mission updated");
      setEditModalOpen(false);
      setSelectedMission(null);
      fetchMissions();
    } catch (err) {
      console.error("❌ Error updating mission:", err);
      toast.error(t("mission.updateError") || "Error updating mission");
    }
  };

  const handleFilter = (filtered) => { setFilteredMissions(filtered); };

  const handleSyncDesBras = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/missions/sync-desbras");
      const { imported, skipped } = res.data || {};
      if (imported > 0) {
        toast.success(t("mission.dbepImported", { count: imported, defaultValue: `${imported} mission(s) importée(s) depuis Des Bras en Plus` }));
      } else {
        toast.info(t("mission.dbepNoNew", { defaultValue: `Aucune nouvelle mission (${skipped ?? 0} déjà à jour)` }));
      }
      fetchMissions();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("mission.dbepError", "Échec de l'import Des Bras en Plus"));
    } finally {
      setSyncing(false);
    }
  };

  const handleCopy = async (mission) => {
    try {
      const payload = {
        type:     mission.type,
        client:   mission.client,
        address:  mission.address || "",
        date:     "",
        priceHT:  mission.priceHT  ? parseFloat(mission.priceHT)  : 0,
        priceTTC: mission.priceHT  ? parseFloat(mission.priceHT) * 1.2 : 0,
        charges:  mission.charges  ? parseFloat(mission.charges)  : 0,
        employees: Array.isArray(mission.employees)
          ? mission.employees
              .filter((e) => e.employee_name?.trim())
              .map((e) => ({ employee_name: e.employee_name.trim(), salary: e.salary ? parseFloat(e.salary) : 0 }))
          : [],
      };
      await api.post("/missions", payload);
      toast.success(t("mission.copied"));
      fetchMissions();
    } catch (err) {
      console.error("❌ Error copying mission:", err?.response?.data || err.message);
      toast.error(t("mission.copyError"));
    }
  };

  const TABS = [
    { key: "all",      label: t("mission.list.tabAll") || "Toutes les missions" },
    { key: "upcoming", label: t("mission.list.tabUpcoming") || "À venir" },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in w-full">
      {/* ── Page header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center text-orange-500 shrink-0">
            <FaTruckMoving className="text-lg" />
          </div>
          <div>
            <h1 className="page-title">{t("mission.title")}</h1>
            <p className="page-subtitle">{missions.length} mission{missions.length !== 1 ? "s" : ""} au total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={handleSyncDesBras}
              disabled={syncing}
              className="px-4 py-2 rounded-xl bg-base-100 border border-base-300 hover:border-primary/40 hover:shadow-sm text-sm font-semibold text-base-content flex items-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              title={t("mission.dbepSyncTitle", "Importer les missions confirmées depuis Des Bras en Plus")}
            >
              <FaDownload className={`text-xs ${syncing ? "animate-pulse" : ""}`} />
              {syncing ? t("mission.dbepSyncing", "Import…") : t("mission.dbepSync", "Importer DBEP")}
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-primary-content text-sm font-semibold shadow-sm transition-colors"
          >
            + {t("mission.form.title") || "Nouvelle mission"}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-base-200 -mt-2">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setViewMode(tab.key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              viewMode === tab.key
                ? "border-orange-500 text-orange-600"
                : "border-transparent text-base-content/60 hover:text-base-content/80 hover:border-base-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── All missions ── */}
      {viewMode === "all" && (
        <>
          <MissionFilter missions={missions} onFilter={handleFilter} />
          <MissionList
            missions={filteredMissions}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onCopy={handleCopy}
            onRefresh={fetchMissions}
          />
        </>
      )}

      {viewMode === "upcoming" && (
        <UpcomingMissions missions={missions} onEdit={handleEdit} />
      )}

      {/* ── Modale création ── */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-[96vw] max-w-3xl bg-base-100 rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col border border-base-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-base-100/95 backdrop-blur border-b border-base-200 px-5 py-3.5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-base-content">
                {t("mission.form.title") || "Nouvelle mission"}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 transition-colors text-lg leading-none"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <MissionForm onCreated={handleCreate} />
            </div>
          </div>
        </div>
      )}

      {editModalOpen && selectedMission && (
        <MissionEditModal
          mission={selectedMission}
          onClose={() => setEditModalOpen(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default MissionsPage;
